import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { createTheme } from '@mui/material/styles';

import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Eager load critical components
import Layout from './components/Layout';
import Login from './components/Login';
import RoleBasedRedirect from './components/RoleBasedRedirect';

// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Categories = lazy(() => import('./pages/Categories'));
const InventoryManagement = lazy(() => import('./pages/InventoryManagement'));
const EquipmentHistory = lazy(() => import('./pages/EquipmentHistory'));
const EquipmentCheckInOut = lazy(() => import('./pages/EquipmentCheckInOut'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Programs = lazy(() => import('./pages/Programs'));
const ProgramCategories = lazy(() => import('./pages/ProgramCategories'));
const Activities = lazy(() => import('./pages/Activities'));
const Komisi = lazy(() => import('./pages/Komisi'));
const PersonaliaKomisi = lazy(() => import('./pages/PersonaliaKomisi'));
const Settings = lazy(() => import('./pages/Settings'));
const GeneralSettings = lazy(() => import('./pages/GeneralSettings'));
const ProposalDetail = lazy(() => import('./pages/ProposalDetail'));
const ProposalReview = lazy(() => import('./pages/ProposalReview'));
const RevisiForm = lazy(() => import('./pages/RevisiForm'));
const BudgetApproval = lazy(() => import('./pages/BudgetApproval'));
const TransferHistory = lazy(() => import('./pages/TransferHistory'));
const ProposalHistory = lazy(() => import('./pages/ProposalHistory'));
const ReviewHistory = lazy(() => import('./pages/ReviewHistory'));
const ApprovalHistory = lazy(() => import('./pages/ApprovalHistory'));
const LPJForm = lazy(() => import('./pages/LPJForm'));
const LPJReview = lazy(() => import('./pages/LPJReview'));
const LPJApproval = lazy(() => import('./pages/LPJApproval'));
const RevisiLPJForm = lazy(() => import('./pages/RevisiLPJForm'));
const DashboardViewDemo = lazy(() => import('./pages/DashboardViewDemo'));

// Loading component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
    }}
  >
    <CircularProgress />
  </Box>
);

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#667eea',
      light: '#8b9bff',
      dark: '#4c63d2',
    },
    secondary: {
      main: '#764ba2',
      light: '#9b6bc7',
      dark: '#5a3680',
    },
    background: {
      default: '#0f1419',
      paper: '#1a1f2e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '0.5px',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '10px',
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #7c8ff5 0%, #8b5db8 100%)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(102, 126, 234, 0.5)',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
        head: {
          fontWeight: 700,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
        filled: {
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.4)',
        },
      },
    },
  },
});

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              
              {/* Demo Page - View Options */}
              <Route path="/view-demo" element={
                <ProtectedRoute>
                  <DashboardViewDemo />
                </ProtectedRoute>
              } />
              
              {/* Root path - redirects based on role */}
              <Route path="/" element={
                <ProtectedRoute>
                  <RoleBasedRedirect />
                </ProtectedRoute>
              } />
              
              {/* Admin Dashboard */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
            
            <Route path="/inventory" element={
              <ProtectedRoute>
                <Layout>
                  <Inventory />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/categories" element={
              <ProtectedRoute>
                <Layout>
                  <Categories />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/inventory-management" element={
              <ProtectedRoute>
                <Layout>
                  <InventoryManagement />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/equipment/history" element={
              <ProtectedRoute>
                <Layout>
                  <EquipmentHistory />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/equipment/movement" element={
              <ProtectedRoute>
                <Layout>
                  <EquipmentCheckInOut />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/history/transfer" element={
              <ProtectedRoute>
                <Layout>
                  <TransferHistory />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/history/proposals" element={
              <ProtectedRoute>
                <Layout>
                  <ProposalHistory />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/history/reviews" element={
              <ProtectedRoute>
                <Layout>
                  <ReviewHistory />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/history/approvals" element={
              <ProtectedRoute>
                <Layout>
                  <ApprovalHistory />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/programs" element={
              <ProtectedRoute>
                <Layout>
                  <Programs />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/program-categories" element={
              <ProtectedRoute>
                <Layout>
                  <ProgramCategories />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/activities" element={
              <ProtectedRoute>
                <Layout>
                  <Activities />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/komisi" element={
              <ProtectedRoute>
                <Layout>
                  <Komisi />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/personalia-komisi" element={
              <ProtectedRoute>
                <Layout>
                  <PersonaliaKomisi />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/settings/general" element={
              <ProtectedRoute>
                <Layout>
                  <GeneralSettings />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/proposals/create" element={
              <ProtectedRoute>
                <Layout>
                  <RevisiForm />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/proposals/edit/:id" element={
              <ProtectedRoute>
                <Layout>
                  <RevisiForm />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/proposals/:id" element={
              <ProtectedRoute>
                <Layout>
                  <ProposalDetail />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/proposals/:id/review" element={
              <ProtectedRoute>
                <ProposalReview />
              </ProtectedRoute>
            } />

            <Route path="/proposals/:id/budget-approval" element={
              <ProtectedRoute>
                <BudgetApproval />
              </ProtectedRoute>
            } />

            <Route path="/proposals/:id/lpj" element={
              <ProtectedRoute>
                <Layout>
                  <LPJForm />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/proposals/:id/lpj/review" element={
              <ProtectedRoute>
                <LPJReview />
              </ProtectedRoute>
            } />

            <Route path="/proposals/:id/lpj/approval" element={
              <ProtectedRoute>
                <Layout>
                  <LPJApproval />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/proposals/:id/lpj/revisi" element={
              <ProtectedRoute>
                <Layout>
                  <RevisiLPJForm />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Redirect all other routes to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
